import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  approveSubmission,
  rejectSubmission,
} from "../utils/firebaseApprovalUtils";
import { useAuth } from "../../../context/AuthContext";
import RejectDialog from "./RejectDialog";

function AdminReviewControls({ folder, onStatusChange }) {
  const { currentUser } = useAuth();
  const [adminNotes, setAdminNotes] = useState(folder.adminNotes || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [dialogStatus, setDialogStatus] = useState("processing"); // "processing", "success", "error"
  const [dialogMessage, setDialogMessage] = useState("");

  const isPending = folder.status === "pending";
  const isReadOnly = !isPending;

  const handleApprove = () => {
    setOpenApproveDialog(true);
    setDialogStatus("processing");
    setDialogMessage("Processing approval...");
    processApproval();
  };

  const processApproval = async () => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    try {
      const adminId = currentUser?.uid;
      await approveSubmission(
        folder.userId,
        folder.folderId,
        adminNotes,
        adminId
      );
      setDialogStatus("success");
      setDialogMessage("Submission approved successfully.");
      setSuccessMessage("Submission approved successfully.");
      if (onStatusChange) onStatusChange();
    } catch (err) {
      setDialogStatus("error");
      setDialogMessage("Failed to approve submission. Please try again.");
      setError("Failed to approve submission. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {
    setOpenRejectDialog(true);
    setDialogStatus("processing");
    setDialogMessage("");
    setError(null);
    setSuccessMessage(null);
  };

  const processRejection = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const adminId = currentUser?.uid;
      await rejectSubmission(
        folder.userId,
        folder.folderId,
        adminNotes,
        adminId
      );
      setSuccessMessage("Submission rejected successfully.");
      setDialogStatus("success");
      setDialogMessage("Submission rejected successfully.");
      if (onStatusChange) onStatusChange();
    } catch (err) {
      setError("Failed to reject submission. Please try again.");
      setDialogStatus("error");
      setDialogMessage("Failed to reject submission. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseApproveDialog = () => {
    setOpenApproveDialog(false);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Admin Review
      </Typography>
      <TextField
        label="Admin Notes"
        multiline
        rows={4}
        fullWidth
        value={adminNotes}
        onChange={(e) => setAdminNotes(e.target.value)}
        disabled={
          isReadOnly || loading || openApproveDialog || openRejectDialog
        }
        sx={{ mb: 2 }}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {isPending && (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={loading || openApproveDialog || openRejectDialog}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={loading || openApproveDialog || openRejectDialog}
          >
            {loading ? <CircularProgress size={24} /> : "Reject"}
          </Button>
        </Box>
      )}
      {!isPending && (
        <Typography variant="body2" color="text.secondary">
          This submission has been {folder.status}.
        </Typography>
      )}

      <Dialog
        open={openApproveDialog}
        onClose={handleCloseApproveDialog}
        disableEscapeKeyDown={dialogStatus === "processing"}
      >
        <DialogTitle>Approval Status</DialogTitle>
        <DialogContent
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 300 }}
        >
          {dialogStatus === "processing" && <CircularProgress />}
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          {dialogStatus !== "processing" && (
            <Button onClick={handleCloseApproveDialog} autoFocus>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <RejectDialog
        open={openRejectDialog}
        onClose={handleCloseRejectDialog}
        onConfirm={processRejection}
        processing={loading}
        error={error}
        success={successMessage}
      />
    </Box>
  );
}

export default AdminReviewControls;
